import { Fragment, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MENU, TACOS_IMG } from "@/data/menu";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.045 } } };
const itemV = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

function MenuItem({ item }) {
  return (
    <motion.div variants={itemV} data-testid={`menu-item-${item.id}`} className="group">
      <div className="flex items-baseline gap-3">
        <h3 className="font-display text-lg sm:text-xl tracking-wide text-[#F5F2EB] uppercase group-hover:text-[#F49D37] transition-colors">
          {item.name}
          {item.tag && (
            <span className="ml-2 align-middle text-[9px] font-body font-bold tracking-[0.15em] uppercase bg-[#D34E36] text-[#F5F2EB] px-2 py-0.5 rounded-full">
              {item.tag}
            </span>
          )}
        </h3>
        <span className="flex-1 border-b border-dotted border-stone-700 -translate-y-1" />
        {item.price && <span className="font-display text-xl text-[#F49D37]">€ {item.price}</span>}
      </div>
      {item.variants && (
        <div className="flex flex-wrap gap-2 mt-2">
          {item.variants.map((v) => (
            <span
              key={v.label}
              className="text-[11px] font-mono2 uppercase tracking-wider border border-[#2A2A34] rounded-full px-3 py-1 text-stone-400"
            >
              {v.label} · <span className="text-[#F49D37]">€ {v.price}</span>
            </span>
          ))}
        </div>
      )}
      {item.desc && <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">{item.desc}</p>}
    </motion.div>
  );
}

function TacosCard() {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <motion.div variants={itemV} className="relative">
        <div className="absolute -inset-6 bg-[#F49D37]/10 blur-3xl rounded-full" aria-hidden="true" />
        <img
          src={TACOS_IMG}
          alt="Tacos francese grigliato con patatine fritte"
          className="relative w-full aspect-[4/3] object-cover rounded-2xl border border-[#2A2A34]"
        />
        <span className="absolute -top-4 -right-2 sm:-right-4 bg-[#D34E36] text-[#F5F2EB] font-display text-3xl px-5 py-1.5 rotate-6 rounded-lg shadow-2xl">
          € 8
        </span>
      </motion.div>
      <motion.div variants={itemV}>
        <h3 className="font-display uppercase text-4xl sm:text-5xl leading-none" data-testid="tacos-title">
          Tacos <span className="text-[#D34E36]">Francesi</span>
        </h3>
        <p className="text-base sm:text-lg text-stone-300 leading-relaxed mt-5">
          Croccanti fuori, golosi dentro. Il tacos alla francese grigliato al momento, servito
          con patatine e salse.
        </p>
        <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-[#F49D37] mt-6">
          Base a scelta: Carne o Pollo
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {["Patatine", "Salsa formaggio", "Salse varie", "Lattuga", "Pomodoro"].map((t) => (
            <span
              key={t}
              className="text-[11px] font-mono2 uppercase tracking-wider border border-[#2A2A34] rounded-full px-3 py-1.5 text-stone-300"
            >
              {t}
            </span>
          ))}
        </div>
        <p className="text-sm text-stone-500 mt-5">In più si possono aggiungere tutti gli ingredienti che vuoi.</p>
      </motion.div>
    </div>
  );
}

export default function MenuSection() {
  const [activeId, setActiveId] = useState(MENU[0].id);
  const active = MENU.find((c) => c.id === activeId);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 sm:py-32 bg-[#141418] grain">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-xs font-mono2 tracking-[0.25em] uppercase text-[#F49D37]"
        >
          Il menù
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4"
        >
          Fame? <span className="text-[#D34E36]">Scegli.</span>
        </motion.h2>

        <div className="flex flex-wrap gap-2 mt-10" role="tablist" aria-label="Categorie del menù">
          {MENU.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={activeId === c.id}
              onClick={() => setActiveId(c.id)}
              data-testid={`menu-category-tab-${c.id}`}
              className={`relative text-[11px] font-mono2 uppercase tracking-[0.15em] px-4 py-2.5 rounded-full border transition-colors ${
                activeId === c.id
                  ? "bg-[#D34E36] border-[#D34E36] text-[#F5F2EB]"
                  : "border-[#2A2A34] text-stone-400 hover:border-[#F49D37] hover:text-[#F49D37]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-12 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              variants={container}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
            >
              {active.special ? (
                <TacosCard />
              ) : (
                <div className="grid md:grid-cols-2 gap-x-14 gap-y-8">
                  {active.items.map((item, idx) => (
                    <Fragment key={item.id}>
                      {item.group && item.group !== active.items[idx - 1]?.group && (
                        <motion.p
                          variants={itemV}
                          className="md:col-span-2 text-xs font-mono2 tracking-[0.25em] uppercase text-[#D34E36] border-b border-[#2A2A34] pb-2 mt-4"
                        >
                          {item.group}
                        </motion.p>
                      )}
                      <MenuItem item={item} />
                    </Fragment>
                  ))}
                </div>
              )}
              {active.note && (
                <motion.p variants={itemV} className="text-sm text-stone-500 italic mt-10 max-w-3xl">
                  {active.note}
                </motion.p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <p
          data-testid="menu-coperto-note"
          className="text-[11px] font-mono2 uppercase tracking-[0.2em] text-stone-500 mt-14 pt-6 border-t border-[#2A2A34]"
        >
          Coperto € 1,50 · * Prodotto surgelato
        </p>
      </div>
    </section>
  );
}
